/* eslint-disable unicorn/consistent-function-scoping */
import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DataState, EXTERNAL_LINKS, routes } from '@libs/utils';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ContractStatus } from '@ffd/api-client';
import { SiteSetupComponentService, StepNumberEnum } from './site-setup.component.service';
const HUBSPOT_CTA_TIMEOUT_MS = 3000;
@Component({
  selector: 'lib-site-setup',
  imports: [CommonModule, RouterModule, TranslateModule, LottieComponent],
  templateUrl: './site-setup.component.html',
  styleUrl: './site-setup.component.scss',
  providers: [SiteSetupComponentService],
})
export class SiteSetupComponent implements OnInit {
  private readonly siteSetupComponentService = inject(SiteSetupComponentService);

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };
  public DataState = DataState;
  public vmSignal = this.siteSetupComponentService.viewModel;

  // The installation-pending step renders the unsupported-PMS blocker, and that layout follows the newer modal design.
  private readonly isAscendEaglesoftBlocker = computed(
    () => this.vmSignal().currentStepFromUser === StepNumberEnum.InstallationPending && this.vmSignal().isUnsupportedPms,
  );

  public isNewModalDesign = computed(
    () =>
      [
        StepNumberEnum.SoftwareLicenseAgreement,
        StepNumberEnum.ServerInstallationChecklist,
        StepNumberEnum.ServerInstallationDownload,
      ].includes(this.vmSignal().currentStepFromUser) || this.isAscendEaglesoftBlocker(),
  );

  // Below xs the card is always full width and auto height; the Figma widths only kick in from xs up.
  public modalClass = computed(() => {
    // Figma: the blocker card is 505px wide with 48px padding, so the content column is 409px.
    if (this.isAscendEaglesoftBlocker()) {
      return 'w-full xs:w-[505px]';
    }

    switch (this.vmSignal().currentStepFromUser) {
      case StepNumberEnum.ServerInstallationChecklist: {
        return 'w-full xs:w-[505px]';
      }

      case StepNumberEnum.ServerInstallationDownload: {
        return 'w-full xs:w-[456px]';
      }

      case StepNumberEnum.ItProviderDetails:
      case StepNumberEnum.InstallOptions:
      case StepNumberEnum.ItProviderContacted: {
        // Card hugs its content but never grows past 80% of the viewport; when content exceeds that,
        // the inner scroll area (see innerHeightClass) takes over.
        return 'w-full xs:w-[505px] max-h-[80%] flex flex-col';
      }

      default: {
        return this.vmSignal().contract?.data?.contractStatus === ContractStatus.SIGNED
          ? 'w-full xs:w-[505px]'
          : 'w-full xs:h-[80%] xs:w-[640px]';
      }
    }
  });

  // The card's padding is split across three nested elements. The blocker needs Figma's flat 48px on all sides,
  // so each level is overridable per step; every other step keeps the original values.
  public outerPaddingClass = computed(() => (this.isAscendEaglesoftBlocker() ? 'p-6 xs:p-12' : 'px-5 pb-3 pt-8 xs:px-[17px] xs:pt-11'));
  public innerPaddingClass = computed(() => (this.isAscendEaglesoftBlocker() ? '' : 'xs:px-[27px]'));
  public contentPaddingClass = computed(() => (this.isAscendEaglesoftBlocker() ? '' : 'pb-8'));

  // Fixed-height cards fill their scroll area (h-full); the content-height IT-provider/install-options
  // cards instead let the scroll area shrink to content (min-h-0) so the card can hug it.
  public innerHeightClass = computed(() => {
    switch (this.vmSignal().currentStepFromUser) {
      case StepNumberEnum.ItProviderDetails:
      case StepNumberEnum.InstallOptions:
      case StepNumberEnum.ItProviderContacted: {
        return 'min-h-0';
      }
      default: {
        return 'h-full';
      }
    }
  });

  public StepNumberEnum = StepNumberEnum;
  public routes = routes;

  public ngOnInit(): void {
    this.siteSetupComponentService.init();
    this.showHubspotCta();
  }

  protected onLogoClick(): void {
    window.open(EXTERNAL_LINKS.FFD_WEBSITE, '_blank');
  }

  private showHubspotCta(): void {
    setTimeout(() => {
      // Add global CSS rule for all HubSpot CTA elements
      const styleId = 'hubspot-cta-transform-fix';
      if (!document.querySelector(`#${styleId}`)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          body #hs-web-interactives-bottom-anchor [id^='hs-overlay-cta-'] {
            /* stylelint-disable-next-line declaration-no-important */
            transform: translateX(0px) !important;
          }
        `;
        document.head.append(style);
      }
    }, HUBSPOT_CTA_TIMEOUT_MS);
  }
}
